/**
 * Credential 加解密工具（混淆等級 obfuscation）
 *
 * 用途：自動登入功能將帳號密碼以「加密形式」存於 cookie，使用前還原為明文。
 *
 * ⚠️ 安全等級：金鑰由 client-public 的 appSecret 衍生（會打包進前端 bundle），
 * 因此僅為「混淆」等級，無法抵擋取得 bundle 的攻擊者還原內容。此為短期前端解的
 * 已知取捨，真正的安全解是後端 refresh token 機制（見 docs/spec/auto-login.md）。
 *
 * 純函式、無 Nuxt/Vue 依賴，可獨立單元測試。
 * crypto.subtle 僅在 client（secure context）與 Node 環境可用，SSR 不可呼叫。
 */

const SALT = 'cuora-auto-login-v1'
const PBKDF2_ITERATIONS = 100000
const IV_LENGTH = 12

const textEncoder = new TextEncoder()
const textDecoder = new TextDecoder()

const toBase64 = (bytes) => {
  const arr = new Uint8Array(bytes)
  let binary = ''
  for (let i = 0; i < arr.length; i++) {
    binary += String.fromCharCode(arr[i])
  }
  return btoa(binary)
}

const fromBase64 = (b64) => {
  const binary = atob(b64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes
}

/**
 * 由字串密鑰衍生 AES-GCM 256 金鑰。
 * @param {string} secret
 * @returns {Promise<CryptoKey>}
 */
export const deriveKey = async (secret) => {
  const baseKey = await crypto.subtle.importKey('raw', textEncoder.encode(String(secret ?? '')), 'PBKDF2', false, ['deriveKey'])
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: textEncoder.encode(SALT), iterations: PBKDF2_ITERATIONS, hash: 'SHA-256' },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  )
}

/**
 * 加密物件 → base64(iv | ciphertext)。
 * @param {object} data
 * @param {CryptoKey} key
 * @returns {Promise<string>}
 */
export const encryptJSON = async (data, key) => {
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH))
  const plaintext = textEncoder.encode(JSON.stringify(data))
  const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, plaintext)
  const combined = new Uint8Array(iv.length + ciphertext.byteLength)
  combined.set(iv, 0)
  combined.set(new Uint8Array(ciphertext), iv.length)
  return toBase64(combined)
}

/**
 * 解密 base64(iv | ciphertext) → 物件；竄改或解密失敗回 null。
 * @param {string} payload
 * @param {CryptoKey} key
 * @returns {Promise<object|null>}
 */
export const decryptJSON = async (payload, key) => {
  try {
    if (!payload) {
      return null
    }
    const combined = fromBase64(payload)
    const iv = combined.slice(0, IV_LENGTH)
    const ciphertext = combined.slice(IV_LENGTH)
    const plaintext = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ciphertext)
    return JSON.parse(textDecoder.decode(plaintext))
  } catch {
    return null
  }
}
